// @givmo/board — the proposals' reads (CBG-AI5-PROPOSE; backend `board-ai-proposals.md` §6.1, §6.2),
// through the host's port on whichever door it calls: the waiting proposed members behind
// `board_member:manage` (the add's own string), the staged batches and one batch behind
// `board_record:import` (the confirmation's). A reader holding neither is served nothing, so neither
// read is asked for them: each runs only for a holder of its string, and every surface built on it
// shows nothing otherwise — absent, never disabled with a note (Jonathan, 2026-09-28).
//
// THE MENU'S TWO COUNTS (Org Machinery OM-30's `countBadge`) read the same cache entries as the
// pages, keyed by the console's active organization through the host's standing hook
// (`useBoardStanding`): the menu draws outside Board's officer host. An act's settle invalidates the
// one root, so a count and its page never disagree.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations, useBoardStanding } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import {
  decodeBatch,
  decodeMemberProposals,
  decodeWaitingBatches,
  type MemberProposal,
  type PastRecordBatch,
  type PastRecordBatchSummary,
} from './proposal-decode';

/** The add's string: a proposed member is shown only to someone who could add them. */
export const MEMBER_PROPOSALS_PERMISSION = 'board_member:manage';
/** The confirmation's string: a batch is shown only to someone who could import it. */
export const PAST_RECORDS_PERMISSION = 'board_record:import';

/** One retry, never for a route miss: a backend that doesn't serve the proposals answers 404. */
function retryUnlessNotServed(failures: number, error: unknown): boolean {
  return !isNotServed(error) && failures < 1;
}

/** The proposals' query keys, rooted per organization. */
export function boardProposalKeys(orgRef: string | null) {
  const root = ['board-proposals', orgRef ?? ''] as const;
  return {
    root,
    members: [...root, 'members'] as const,
    batches: [...root, 'batches'] as const,
    batch: (batchId: string) => [...root, 'batch', batchId] as const,
  };
}

function useMemberProposalsRead(orgRef: string | null, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardProposalKeys(orgRef).members,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<MemberProposal[]> => {
      const { data } = await operations.officerListBoardMemberProposals({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return decodeMemberProposals(data);
    },
  });
}

function useWaitingBatchesRead(orgRef: string | null, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardProposalKeys(orgRef).batches,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<PastRecordBatchSummary[]> => {
      const { data } = await operations.officerListBoardPastRecordBatches({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return decodeWaitingBatches(data);
    },
  });
}

/** The waiting proposed members (frame r1), or none for a reader who can't add a member. */
export function useMemberProposals(): MemberProposal[] {
  const { orgRef, isReady, permissions } = useBoardHost();
  const allowed = hasBoardPermission(permissions, MEMBER_PROPOSALS_PERMISSION);
  const read = useMemberProposalsRead(orgRef, isReady && allowed);
  return allowed ? (read.data ?? []) : [];
}

/** The batches waiting (frames i1, m1), or none for a reader who can't import them. */
export function useWaitingBatches(): PastRecordBatchSummary[] {
  const { orgRef, isReady, permissions } = useBoardHost();
  const allowed = hasBoardPermission(permissions, PAST_RECORDS_PERMISSION);
  const read = useWaitingBatchesRead(orgRef, isReady && allowed);
  return allowed ? (read.data ?? []) : [];
}

/** One batch, as its page reads it. A 404 is the batch's absence, an answer the page acts on. */
export function useProposedBatch(batchId: string) {
  const operations = useBoardOperations();
  const { orgRef, isReady, permissions } = useBoardHost();
  const allowed = hasBoardPermission(permissions, PAST_RECORDS_PERMISSION);
  return useQuery({
    queryKey: boardProposalKeys(orgRef).batch(batchId),
    enabled: isReady && allowed && orgRef !== null,
    retry: false,
    queryFn: async (): Promise<PastRecordBatch | null> => {
      const { data } = await operations.officerGetBoardPastRecordBatch({
        path: { org_ref: orgRef ?? '', batch_id: batchId },
        throwOnError: true,
      });
      return decodeBatch(data);
    },
  });
}

/** After any act on a proposal: every proposals read again, the menu's counts with them. */
export function useSettleProposals() {
  const queryClient = useQueryClient();
  const { orgRef } = useBoardHost();
  return () => queryClient.invalidateQueries({ queryKey: boardProposalKeys(orgRef).root });
}

/**
 * The menu's count beside Board members: the proposed members waiting. `undefined` (the shell draws
 * nothing) while unread, failed, or for someone who can't add a member.
 */
export function useWaitingMemberCount(): number | undefined {
  const { orgRef, permissions } = useBoardStanding();
  const allowed = hasBoardPermission(permissions, MEMBER_PROPOSALS_PERMISSION);
  const read = useMemberProposalsRead(orgRef, allowed);
  return allowed ? read.data?.length : undefined;
}

/**
 * The menu's count beside Minute book: the past records waiting for a person to confirm them,
 * summed over the sets they wait in, so it says what the pages say ("Proposed, waiting for you
 * (4)"); a count of sets read "1" beside four records (cut inbox's review fold, M9).
 */
export function useWaitingBatchCount(): number | undefined {
  const { orgRef, permissions } = useBoardStanding();
  const allowed = hasBoardPermission(permissions, PAST_RECORDS_PERMISSION);
  const read = useWaitingBatchesRead(orgRef, allowed);
  return allowed ? read.data?.reduce((sum, batch) => sum + batch.staged_count, 0) : undefined;
}
